import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import ReactDOM from "react-dom";
import CustomIconButton from "./CustomIconButton";
import TextInput from "./TextInput";
import CustomButton from "./CustomButton";

interface Option {
  id: number | string;
  name: string;
  icon?: string | null;
}

interface OptionValue {
  id: number | string | null;
  name: string;
  icon?: string | null;
}

interface DropdownProps {
  extraLabel?: string;
  dropdownPosition?: string;
  optionsAnchor?: string;
  fontWeight?: "normal" | "bold";
  inputWidth?: "" | "full";
  type?: "default" | "custom" | "border" | "text";
  disabled?: boolean;
  placeholder?: string;
  value?: OptionValue | OptionValue[] | null;
  options?: Option[];
  onChange?: (option: Option | Option[] | null) => void;
  customStyle?: string;
  customStyleOption?: string;
  size?: string;
  error?: string;
  errorText?: string;
  customRef?: React.RefObject<HTMLDivElement>;
  unitLabel?: string;
  isUnitLabelHavingMargin?: boolean;
  havingIcon?: boolean;
  multiple?: boolean;
  optionStyles?: string;
  clearable?: boolean;
  searchInput?: boolean;
  searchQuery?: string;
  setSearchQuery?: (query: string) => void;
  actionsButton?: { name: string; variant: 'default' | 'outline' | 'gray' | 'text'; handleClick: () => void}[];
  noOptionsFoundText?: string;
  isValuesTruncated?: boolean;
  isMinHeight?: boolean;
  optionsFixedWidth?: boolean;
}

const Dropdown: React.FC<DropdownProps> = ({
  extraLabel = "",
  dropdownPosition = "",
  optionsAnchor = "left",
  fontWeight = "normal",
  inputWidth = "",
  type = "default",
  disabled = false,
  placeholder = "",
  value = null,
  options = [],
  onChange = () => {},
  customStyle = "",
  customStyleOption = "",
  size = "md",
  error = "",
  errorText = "",
  customRef = null,
  unitLabel = "",
  isUnitLabelHavingMargin = true,
  havingIcon = true,
  multiple = false,
  optionStyles = "",
  clearable = false,
  searchInput = false,
  searchQuery = "",
  setSearchQuery = () => {},
  actionsButton = [],
  noOptionsFoundText = "",
  isValuesTruncated = true,
  isMinHeight = false,
  tabindex=-1,
  dropdownRef=useRef<HTMLDivElement>(null),
  optionsFixedWidth = false
}) => {
  const [open, setOpen] = useState(false);
  const dropdownListRef = useRef<HTMLUListElement>(null);
  const mouseDownInsideRef = useRef(false); // track if click happened inside portal
  const [dropdownStyles, setDropdownStyles] = useState<React.CSSProperties>({
    position: "absolute",
    visibility: "hidden",
    opacity: 0,
    left: "-9999px",
    top: "-9999px",
  });
  const [focused, setFocused] = useState(false);

  const styleBasedSizeProp: Record<string, string> = {
    md: `py-[0.375rem] px-3 ${isMinHeight ? "min-h-[2.25rem]": "h-[2.25rem]"} ${
      inputWidth === "full" ? "w-full" : "min-w-[13.5rem]"
    }`,
    filter: `px-3 py-1 ${isMinHeight ? "min-h-[1.75rem]": "h-[1.75rem]"} ${
      inputWidth === "full" ? "w-full" : "min-w-[13.5rem]"
    }`,
  };

  const handleSelect = (option: Option) => {
    if (!multiple) {
      const currentValue = value as Option | null;
      if (currentValue && currentValue.id === option.id) {
        setOpen(false);
        setFocused(false);
        return; 
      }

      onChange(option);
      setOpen(false);
      setFocused(false);
    } else {
      const selectedOptions = Array.isArray(value)
        ? [...(value as OptionValue[])]
        : [];
      const existingOption = selectedOptions.find((opt) => opt.id === option.id);
      if (existingOption) {
        const updatedOptions = selectedOptions.filter(
          (opt) => opt.id !== existingOption.id
        );
        updatedOptions.length > 0 ? onChange(updatedOptions) : onChange(null);
      } else {
        const updatedOptions = [...selectedOptions, option];
        onChange(updatedOptions);
      }
    }
  };

  const handleBlur = (event: any) => {
    setFocused(false);

    // if focus stayed inside trigger wrapper → keep open
    if (event.currentTarget.contains(event.relatedTarget)) return;

    // if active element is inside dropdown portal → keep open
    if (
      dropdownListRef.current &&
      dropdownListRef.current.contains(document.activeElement)
    ) {
      return;
    }

    // if mouse down was inside portal → keep open
    if (mouseDownInsideRef.current) {
      setTimeout(() => (mouseDownInsideRef.current = false), 0);
      return;
    }

    // otherwise close
    setOpen(false);
  };

  const openDropdown = () => {
    setDropdownStyles((prev) => ({
      ...prev,
      visibility: "hidden",
      opacity: 0,
      left: "-9999px",
      top: "-9999px",
    }));
    setOpen(true);
  };

  const setCombinedRef = (el: HTMLUListElement | null) => {
    dropdownListRef.current = el;
    if (customRef && typeof customRef !== "function") {
      (customRef as React.MutableRefObject<HTMLUListElement | null>).current =
        el;
    } else if (typeof customRef === "function") {
      customRef(el);
    }
  };

  const updateDropdownPosition = () => {
    if (dropdownRef.current && dropdownListRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const dropdownHeight = dropdownListRef.current.offsetHeight;
      const scrollY = window.scrollY;
      const scrollX = window.scrollX;

      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      let top: number;

      if (spaceBelow >= dropdownHeight) {
        top = rect.bottom + 8 + scrollY;
      } else if (spaceAbove >= dropdownHeight) {
        top = rect.top + scrollY - dropdownHeight - 8;
      } else {
        top = rect.bottom + 8 + scrollY;
      }

      const left = rect.left + scrollX;

      setDropdownStyles({
        position: "absolute",
        top: `${top}px`,
        left: `${left}px`,
        width: rect.width,
        zIndex: 9999,
        visibility: "visible",
        opacity: 1,
        transition: "opacity 120ms ease-out",
      });
    }
  };
  
  const handleSearch = (e: any) => {
    if (e.key === "Enter") {
      console.log("search ....", searchQuery);
    }
  };

  useLayoutEffect(() => {
    if (!open) return;

    const handlePosition = () => {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(updateDropdownPosition);
      });
    };

    handlePosition();
    window.addEventListener("resize", updateDropdownPosition);
    window.addEventListener("scroll", updateDropdownPosition, true);

    return () => {
      window.removeEventListener("resize", updateDropdownPosition);
      window.removeEventListener("scroll", updateDropdownPosition, true);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open) return;
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(updateDropdownPosition);
    });
  }, [value, open]);

  useEffect(() => {
    if (!open) {
      setSearchQuery("");
      return;
    }

    function handleClickOutside(e: MouseEvent) {
      const triggerEl = dropdownRef.current;
      const listEl = dropdownListRef.current;
      const target = e.target as Node;

      if (
        triggerEl &&
        !triggerEl.contains(target) &&
        listEl &&
        !listEl.contains(target)
      ) {
        setOpen(false);
        setFocused(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  const safeOptions = Array.isArray(options) ? options : [];

  const dropdown = (
    <ul
      className={`${optionsFixedWidth ? "min-w-auto" : "min-w-auto md:min-w-max"} py-1 bg-white alu-options-box-shadow rounded custom-scrollbar ${
        customStyleOption || ""
      }`}
      style={dropdownStyles}
      ref={setCombinedRef}
      onMouseDown={() => {
        mouseDownInsideRef.current = true;
      }}
      onMouseUp={() => {
        setTimeout(() => (mouseDownInsideRef.current = false), 0);
      }}
    >
      {searchInput && <div className="px-3 py-[0.375rem]">
        <TextInput
          value={searchQuery}
          onChange={(e: any) => setSearchQuery(e.target.value)}
          onClearQuery={() => setSearchQuery("")}
          onKeyDown={(e: any) => handleSearch(e)}
          placeholder="検索"
          searchIcon={true}
          customClassName="!h-[28px] !text-xs"
          size={"filter"}
          tabindex={tabindex}
        />
      </div>}

      {
        safeOptions.length > 0 ? <div className="overflow-x-hidden overflow-y-auto max-h-[18.75rem] px-1 custom-scrollbar">
          {safeOptions.map((option, idx) => (
            <li
              tabIndex={0}
              key={idx}
              className={`px-2 py-[0.375rem] rounded flex items-center gap-2 hover:bg-[#FFF3E5] hover:cursor-pointer ${
                option.disabled
                  ? "!text-[#BBB] !bg-[#F7F7F7] hover:!bg-[#F7F7F7] hover:!cursor-default"
                  : ""
              } ${option.hidden ? "hidden" : ""}`}
              onClick={() => {
                !option.disabled && handleSelect(option);
              }}
            >
              {option?.id === value?.id ||
              (Array.isArray(value) &&
                value?.some((selected) => selected?.id === option.id)) ? (
                <span className="shrink-0">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M8.9999 16.2001L4.7999 12.0001L3.3999 13.4001L8.9999 19.0001L20.9999 7.0001L19.5999 5.6001L8.9999 16.2001Z"
                      fill="#FF861A"
                    />
                  </svg>
                </span>
              ) : (
                <span className="h-4 w-4 shrink-0" />
              )}
              <div className="flex items-center">
                {option?.icon && <span className="mr-1">{option.icon}</span>}
                <p
                  className={`flex-1 text-sm leading-6 font-normal alu-text-base-2 ${
                    optionStyles || ""
                  }`}
                >
                  {option.name}
                  {unitLabel && <span className="ml-[2px]">{unitLabel}</span>}
                </p>
              </div>
            </li>
          ))}
      </div>
        : <p className="px-4 py-2 text-sm alu-text-base-1">{noOptionsFoundText}</p>
      }

      {actionsButton && actionsButton.length > 0 && (
        <div className="mt-1 border-t pt-2 pb-1 flex justify-center items-center">
          {actionsButton.map((button,idx) => (
            <CustomButton key={idx} size="sm" variant={button?.variant} onClick={() => {setOpen(false); button.handleClick();}}>{button?.name}</CustomButton>
          ))}
        </div>
      )}
    </ul>
  );

  return (
    <>
      <div
        className={`relative flex ${
          dropdownPosition === "center"
            ? "justify-center"
            : dropdownPosition === "right"
            ? "justify-end"
            : "justify-start"
        }`}
      >
        <div
          ref={dropdownRef}
          tabIndex={tabindex}
          onClick={() => {
            if (open) {
              setOpen(false);
            } else {
              openDropdown();
            }
          }}
          onFocus={() => setFocused(true)}
          className={`${
            !extraLabel
              ? `${styleBasedSizeProp[size]} flex items-center pr-2`
              : `py-[0.375rem] px-3 ${
                  inputWidth === "full" ? "w-full" : "min-w-[13.5rem]"
                }`
          } group gap-2 ${
            fontWeight === "bold" ? "font-bold" : "font-normal"
          } text-sm outline-0 border border-[#E0E0E0] rounded ${
            disabled
              ? "bg-[#F7F7F7] text-[#BBB] hover:cursor-default"
              : error
              ? "border-2 border-[#F33D62] hover:border-[#F33D62] hover:bg-[#FFF3E5] cursor-pointer"
              : `bg-white hover:cursor-pointer focus:border-[#FF861A] hover:bg-[#FFF3E5] hover:border-transparent ${
                  extraLabel
                    ? "hover:bg-[#FFEAD2]"
                    : Array.isArray(value)
                    ? value.length === 0
                      ? "hover:bg-[#FFF3E5] hover:border-transparent"
                      : focused && open
                      ? "border-[#FF861A] bg-white"
                      : ""
                    : !value?.name
                    ? "hover:bg-[#FFF3E5] hover:border-transparent"
                    : focused && open
                    ? "border-[#FF861A] bg-white"
                    : ""
                }`
          } ${customStyle} outer has-[.inner:hover]:bg-white has-[.inner:hover]:border-[#E0E0E0]`}
        >
          {extraLabel && (
            <p className="text-sm alu-text-base-1 text-center mb-1">
              {extraLabel}
            </p>
          )}
          <div className={`flex items-center justify-between w-full gap-1`}>
            {!multiple ? (
              <div
                className={`flex-1 flex items-center ${
                  extraLabel ? "font-bold" : ""
                }`}
              >
                {value?.icon && <span className="mr-1">{value.icon}</span>}
                <p
                  className={`${
                    optionStyles && value?.name ? optionStyles : ""
                  } ${
                    !disabled ? "group-hover:text-[#343A3D]" : "!text-[#BBB]"
                  } ${
                    !value && !extraLabel
                      ? "alu-text-base-1"
                      : "alu-text-base-2"
                  } ${size === "md" ? "text-sm" : "text-xs"} mr-auto line-clamp-1 overflow-hidden text-ellipsis`}
                >
                  {value?.name ? (
                    <>
                      {value.name}
                      {unitLabel && (
                        <span
                          className={`${
                            isUnitLabelHavingMargin ? "ml-[2px]" : ""
                          }`}
                        >
                          {unitLabel}
                        </span>
                      )}
                    </>
                  ) : (
                    placeholder
                  )}
                </p>
                {value?.name && clearable && (
                  <CustomIconButton
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onChange(null);
                    }}
                    customStyle="inner cursor-pointer !shrink-0 !h-5 !w-5"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <g clipPath="url(#clip0_8574_265)">
                        <path
                          d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z"
                          fill="currentColor"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_8574_265">
                          <rect width="24" height="24" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                  </CustomIconButton>
                )}
              </div>
            ) : (
              <div className="flex-1 flex items-start gap-1 justify-between overflow-hidden">
                <div className="w-[90%] flex items-center gap-[0.375rem] overflow-hidden">
                  {Array.isArray(value) && value.length > 0 ? (
                    optionStyles ? (
                      <div className={`flex items-center gap-x-[0.625rem] gap-y-[0.375rem] ${isValuesTruncated ? "overflow-hidden truncate" : "flex-wrap"}`}>
                        {value.map((v, idx) => (
                          <div
                            key={idx}
                            className={`${optionStyles} text-sm alu-text-base-2 shrink-0`}
                          >
                            {v.icon && <span className="mr-1">{v.icon}</span>}
                            {v.name}
                            {unitLabel && (
                              <span
                                className={
                                  isUnitLabelHavingMargin ? "ml-[2px]" : ""
                                }
                              >
                                {unitLabel}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="line-clamp-1 overflow-hidden text-ellipsis text-sm leading-[22px] alu-text-base-2">
                        {value
                          .map((v) => v.name + (unitLabel ? unitLabel : ""))
                          .join(", ")}
                      </p>
                    )
                  ) : (
                    placeholder
                  )}
                </div>
                {Array.isArray(value) && value.length > 0 && !open && (
                  <CustomIconButton
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onChange(null);
                    }}
                    customStyle="inner cursor-pointer !shrink-0 !h-5 !w-5 !mr-[1px] mt-[1px]"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <g clipPath="url(#clip0_8574_265)">
                        <path
                          d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z"
                          fill="currentColor"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_8574_265">
                          <rect width="24" height="24" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                  </CustomIconButton>
                )}
              </div>
            )}
            {havingIcon && (
              <span
                className="shrink-0"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen((prev) => !prev);
                }}
              >
                <svg
                  className={`${
                    !disabled
                      ? "text-[#808080] group-hover:text-[#FF861A]"
                      : "text-[#BBB]"
                  }`}
                  xmlns="http://www.w3.org/2000/svg"
                  width={size === "filter" || type === "text" ? "16" : "24"}
                  height={size === "filter" || type === "text" ? "16" : "24"}
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M16.59 8.59L12 13.17L7.41 8.59L6 10L12 16L18 10L16.59 8.59Z"
                    fill="currentColor"
                  />
                </svg>
              </span>
            )}
          </div>
        </div>
      </div>

      {!disabled &&
        open &&
        ReactDOM.createPortal(
          dropdown,
          document.getElementById("dropdown-root") as HTMLElement
        )}

      {error && errorText != "" && (
        <p className="alu-text-error text-sm mt-2">
          {errorText ? `*${errorText}` : "＊エラーテキストが入ります"}
        </p>
      )}
    </>
  );
};

export default Dropdown;